<template>
  <v-dialog
    :value="show"
    max-width="60vw"
    scrollable
    @input="cancel"
  >
    <v-card
      flat
      class="pa-3 mt-2"
    >
      <v-card-title class="justify-center text-h5">
        {{ label }}
      </v-card-title>
      <v-card-text>
        <v-form
          ref="form"
          class="multi-col-validation mt-4"
        >
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="name"
                :rules="[rules.required]"
                :error-messages="messages('name')"
                label="Название"
                hint="Видно только в админке — по нему рассылку ищут в списке"
                persistent-hint
                dense
                outlined
              ></v-text-field>
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <date-time-picker
                v-if="!sendNow"
                v-model="scheduledAt"
                label="Время отправки"
                place-holder="Выберите дату и время"
                clear-text="Очистить"
                :rules="[rules.required]"
                :text-field-props="scheduledAtProps"
              >
              </date-time-picker>
              <v-checkbox
                v-model="sendNow"
                label="Отправить сразу"
                hide-details
                dense
                class="mt-0"
              ></v-checkbox>
            </v-col>

            <v-col cols="12">
              <v-divider class="mb-2"></v-divider>
              <span class="text-subtitle-2">Куда слать</span>
              <v-checkbox
                v-model="toApp"
                label="В приложение"
                hide-details
                dense
              ></v-checkbox>
              <v-checkbox
                v-model="toBrowser"
                label="В браузер (всем подписавшимся на сайте)"
                hide-details
                dense
              ></v-checkbox>
              <div
                v-if="hasError('toApp')"
                class="text-caption error--text mt-2"
              >
                {{ messages('toApp') }}
              </div>
            </v-col>

            <v-col cols="12">
              <v-divider class="mb-2"></v-divider>
              <span class="text-subtitle-2">Текст</span>
              <v-alert
                v-if="hasError('texts')"
                type="error"
                text
                dense
                class="mt-2 mb-0"
              >
                {{ messages('texts') }}
              </v-alert>

              <v-tabs v-model="textTab">
                <v-tab
                  v-for="language in languages"
                  :key="language.code"
                >
                  {{ language.name }}
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="textTab">
                <v-tab-item
                  v-for="text in texts"
                  :key="text.language"
                  eager
                >
                  <v-text-field
                    v-model="text.title"
                    :rules="textRules(text)"
                    label="Заголовок"
                    counter="100"
                    maxlength="100"
                    dense
                    outlined
                    class="mt-4"
                  ></v-text-field>
                  <v-textarea
                    v-model="text.body"
                    :rules="textRules(text)"
                    label="Сообщение"
                    counter="500"
                    maxlength="500"
                    rows="3"
                    dense
                    outlined
                  ></v-textarea>
                </v-tab-item>
              </v-tabs-items>
              <span class="text-caption text--secondary">
                Если текст на языке не заполнен, гость с этим языком получит русский
              </span>
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="link"
                :error-messages="messages('link')"
                label="Ссылка"
                hint="Путь на сайте, например /promotions, или полный адрес с https://"
                persistent-hint
                dense
                outlined
              ></v-text-field>
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="imageUrl"
                :error-messages="messages('imageUrl')"
                label="Картинка"
                hint="Адрес картинки с https://"
                persistent-hint
                dense
                outlined
              ></v-text-field>
            </v-col>

            <template v-if="toApp">
              <v-col cols="12">
                <v-divider class="mb-2"></v-divider>
                <span class="text-subtitle-2">Кому в приложение</span>
                <v-alert
                  v-if="hasError('recipients')"
                  type="error"
                  text
                  dense
                  class="mt-2 mb-0"
                >
                  {{ messages('recipients') }}
                </v-alert>
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-autocomplete
                  v-model="groupCriteriaIds"
                  :items="groupCriteriaDropdown"
                  label="Группы пользователей"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  hide-details
                  dense
                  outlined
                ></v-autocomplete>
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <async-multi-select
                  v-model="customerIds"
                  :items="customersDropdown"
                  :search="fetchCustomers"
                  label="Гости — поиск по номеру телефона"
                >
                </async-multi-select>
              </v-col>

              <v-col cols="12">
                <v-file-input
                  v-model="file"
                  label="Файл CSV: телефон в первой колонке каждой строки"
                  dense
                  accept=".csv,text/csv"
                  outlined
                  hide-details
                  @change="fileUploaded"
                ></v-file-input>
              </v-col>

              <v-col
                v-if="phoneNumbers.length"
                cols="12"
              >
                <span>Номеров в файле: {{ phoneNumbers.length }} — {{ phonesPreview }}</span>
              </v-col>

              <v-col
                v-if="fileCustomerIds.length"
                cols="12"
              >
                <span>Из файла ранее: {{ fileCustomerIds.length }} гостей</span>
                <v-btn
                  small
                  text
                  class="ms-2"
                  @click="fileCustomerIds = []"
                >
                  Очистить
                </v-btn>
              </v-col>
            </template>

            <v-col cols="12">
              <v-btn
                outlined
                small
                :loading="estimating"
                @click="estimateAudience"
              >
                <v-icon
                  size="18"
                  class="me-2"
                >
                  {{ icons.mdiAccountGroupOutline }}
                </v-icon>
                <span>Оценить аудиторию</span>
              </v-btn>

              <div
                v-if="estimate"
                class="d-flex flex-wrap mt-4"
              >
                <div
                  v-for="row in estimateRows"
                  :key="row.field"
                  class="me-6 mb-2"
                >
                  <div class="text--secondary text-caption">
                    {{ row.text }}
                  </div>
                  <div class="text-subtitle-1 font-weight-medium">
                    {{ estimate[row.field] }}
                  </div>
                </div>
              </div>
            </v-col>

            <v-col cols="12">
              <v-btn
                color="primary"
                class="me-3 mt-4"
                @click="saveData"
              >
                Сохранить
              </v-btn>
              <v-btn
                color="secondary"
                outlined
                class="mt-4"
                type="reset"
                @click="cancel"
              >
                Отмена
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { mdiAccountGroupOutline } from '@mdi/js'
import { mapFormHandlers } from '@/helpers/forms'
import DateTimePicker from '@/components/date-time-picker'
import AsyncMultiSelect from '@/components/async-multi-select'
import { rules } from '@/helpers/validation'
import { hasErrorsForProperty, errorMessages } from '@/helpers'
import { notificationLanguages } from '@/helpers/notifications'

const PHONES_PREVIEW = 10

export default {
  name: 'NotificationCampaignForm',
  components: { DateTimePicker, AsyncMultiSelect },
  props: ['save', 'show', 'cancellation', 'label'],
  data: () => ({
    icons: { mdiAccountGroupOutline },
    rules,
    languages: notificationLanguages,
    textTab: 0,
    file: null,
    estimateRows: [
      { field: 'customers', text: 'Гостей в списке' },
      { field: 'customersReachable', text: 'Получат (с устройством и без отказа)' },
      { field: 'customersUnsubscribed', text: 'Отказались от рассылок' },
      { field: 'customersWithoutDevice', text: 'Без устройства' },
      { field: 'appDevices', text: 'Устройств в приложении' },
      { field: 'browserDevices', text: 'Подписанных браузеров' },
      { field: 'phonesNotFound', text: 'Номеров из файла не найдено' },
    ],
  }),
  computed: {
    ...mapFormHandlers('notificationCampaign/form', [
      'name',
      'scheduledAt',
      'sendNow',
      'toApp',
      'toBrowser',
      'link',
      'imageUrl',
      'texts',
      'groupCriteriaIds',
      'customerIds',
      'fileCustomerIds',
      'estimate',
    ]),
    ...mapState('notificationCampaign/form', ['phoneNumbers', 'estimating']),
    ...mapGetters('groupCriteria', { groupCriteriaDropdown: 'dropdown' }),
    ...mapGetters('dictionary', { customersDropdown: 'customerDropdown' }),

    scheduledAtProps() {
      return {
        errorMessages: this.messages('scheduledAt'),
        hint: 'Время — по Бишкеку',
        persistentHint: true,
      }
    },
    phonesPreview() {
      const shown = this.phoneNumbers.slice(0, PHONES_PREVIEW).join(', ')
      const rest = this.phoneNumbers.length - PHONES_PREVIEW

      return rest > 0 ? `${shown} и ещё ${rest}` : shown
    },

    audience() {
      return JSON.stringify([
        this.toApp,
        this.toBrowser,
        this.groupCriteriaIds,
        this.customerIds,
        this.fileCustomerIds,
        this.phoneNumbers,
      ])
    },
  },
  watch: {
    show(value) {
      if (!value) return

      this.textTab = 0
      this.file = null
      if (this.$refs.form) this.$refs.form.resetValidation()

      this.fetchGroupCriteria()
      if (this.customerIds.length) this.fetchCustomers({ ids: this.customerIds })
    },
    audience() {
      this.estimate = null
    },
  },
  methods: {
    ...mapActions('notificationCampaign/form', {
      resetForm: 'resetForm',
      previewPhoneNumbers: 'previewPhoneNumbers',
      estimateAudience: 'estimate',
    }),
    ...mapActions('groupCriteria', { fetchGroupCriteria: 'fetchDropdown' }),
    ...mapActions('dictionary', { fetchCustomers: 'fetchCustomersDropdown' }),
    hasError(property) {
      return hasErrorsForProperty(this.errors, property)
    },
    messages(property) {
      return errorMessages(this.errors, property)
    },

    textRules(text) {
      return text.language === 'ru' ? [rules.required] : []
    },
    async fileUploaded(file) {
      await this.previewPhoneNumbers(file)
    },
    cancel() {
      this.resetForm()
      this.cancellation()
    },
    async saveData() {
      if (!this.$refs.form.validate()) {
        this.textTab = 0

        return
      }

      await this.save()
      this.resetForm()
    },
  },
}
</script>
